"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNewlyAssignedExtendedResponseTape = exports.createExtendedResponseTape = exports.generateExtendedResponseTapeFieldNames = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const progressHelper_1 = require("./progressHelper");
const RequiredIntermediateFields_1 = require("./RequiredIntermediateFields");
// @required-fields
// valley bank wants advanceRatePath in their extended response tape, need to hardcode these for now
const env = common_1.utils.EnvUtils.getEnv();
// static manual field names
const concentrationLimitFieldsTotalConcentrationReductionField = 'concentrationLimitFields_totalConcentrationReduction';
const assignmentFieldsReceivableStatusFieldName = 'assignmentFields_receivableStatus';
const externalReceivableIdFieldName = 'externalReceivableId';
const finleyReceivableIdFieldName = 'finleyReceivableId';
const valleyBankLoanProductIds = {
    // same as stage, assuming this was dumped.
    development: [
        '40941652-b01f-400e-a215-0bf7459ab3bf', // deposits
        '15618a52-8f23-4284-8b3e-fbd955c41079', // securities
    ],
    stage: [
        '40941652-b01f-400e-a215-0bf7459ab3bf', // deposits
        '15618a52-8f23-4284-8b3e-fbd955c41079', // securities
    ],
    prod: [
        '1cde1a2f-d558-4f01-86fc-c48178fd1105', // deposits
        'a12c120c-e7ff-40e1-a315-9d70f769714d', // securities
    ],
    'client-uat': [],
    client: [],
};
const getValleyBankLoanProductIds = () => valleyBankLoanProductIds[env];
// @required-fields
// eventually we want to allow these into extended response tape,
// we need to carefully manage template rollover
const EXCLUDED_BORROWING_BASE_FIELD_NAMES = [
    'advanceRatePath',
];
// @required-fields
// eventually we want to allow these into extended response tape,
// we need to carefully manage template rollover
const EXCLUDED_REQUIRED_INTERMEDIATE_FIELD_NAMES = [
    'normalizedDaysPastDue',
    'normalizedDurationDays',
    'normalizedisChargedOff',
    'normalizedisDelinquent',
    'normalizedOriginationDate',
    'normalizedInitialBalance',
];
// Assignment Fields
const generallySupportedAssignmentFields = [
    'assignedAt',
];
const forwardFlowSupportedAssignmentFields = [
    'purchasePercentageRate',
    'purchasePrice',
];
const generateFieldName = ({ fieldPrefix, fieldSuffix, }) => {
    if (!fieldPrefix) {
        return fieldSuffix;
    }
    return `${fieldPrefix}_${fieldSuffix}`;
};
const generateExtendedResponseTapeFieldNames = ({ baseFields, intermediateFields, eligibilityPropertyKeys, allConcentrationLimitKeys, }) => {
    // initiate with manual fields at front of extended response tape
    const fieldNames = [
        externalReceivableIdFieldName,
        finleyReceivableIdFieldName,
    ];
    // base fields
    baseFields.forEach((field) => {
        fieldNames.push(generateFieldName({ fieldPrefix: 'baseFields', fieldSuffix: field }));
    });
    // required and dynamic intermediate fields
    intermediateFields.forEach((field) => {
        const prefix = RequiredIntermediateFields_1.RequiredIntermediateFieldNames.includes(field)
            ? 'requiredIntermediateFields'
            : 'dynamicIntermediateFields';
        fieldNames.push(generateFieldName({ fieldPrefix: prefix, fieldSuffix: field }));
    });
    // post processed fields, only normalizedExposure supported currently
    fieldNames.push(generateFieldName({
        fieldPrefix: 'postProcessedFields',
        fieldSuffix: 'normalizedExposure',
    }));
    // Valuation fields
    Object.keys(finley_types_1.Receivable.CreditFacility.ValuationBaseFieldsIterable).forEach((key) => {
        fieldNames.push(generateFieldName({ fieldPrefix: 'valuationFields', fieldSuffix: key }));
    });
    // Elig Status fields
    eligibilityPropertyKeys.forEach((key) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'eligibilityStatus',
            fieldSuffix: key,
        }));
    });
    // Elig Fields
    Object.keys(finley_types_1.Receivable.CreditFacility.EligibilityBaseFieldsIterable).forEach((key) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'eligibilityFields',
            fieldSuffix: key,
        }));
    });
    // conc limit tests
    allConcentrationLimitKeys.forEach((key) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'concentrationLimitFields',
            fieldSuffix: key,
        }));
    });
    // total conc reduction
    fieldNames.push(concentrationLimitFieldsTotalConcentrationReductionField);
    // Conc limit Fields
    Object.keys(finley_types_1.Receivable.CreditFacility.ConcentrationLimitBaseFieldsIterable).forEach((key) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'concentrationLimitFields',
            fieldSuffix: key,
        }));
    });
    // borrowing base fields
    Object.keys(finley_types_1.Receivable.CreditFacility.BorrowingBaseBaseFieldsIterable).forEach((key) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'borrowingBaseFields',
            fieldSuffix: key,
        }));
    });
    // assignment fields
    generallySupportedAssignmentFields.forEach((field) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'assignmentFields',
            fieldSuffix: field,
        }));
    });
    // manually added field, adding here so renders in order of how it appears in rrt
    fieldNames.push(assignmentFieldsReceivableStatusFieldName);
    forwardFlowSupportedAssignmentFields.forEach((field) => {
        fieldNames.push(generateFieldName({
            fieldPrefix: 'assignmentFields',
            fieldSuffix: field,
        }));
    });
    return fieldNames;
};
exports.generateExtendedResponseTapeFieldNames = generateExtendedResponseTapeFieldNames;
const createAssignedCsvFieldAdapter = ({ assignedLoanTapeCsvConfig, }) => {
    // always use field if assignedLoanTapeCsvConfig is not configued
    if (!assignedLoanTapeCsvConfig) {
        return () => true;
    }
    // eagerly create fieldSet
    const fieldSet = new Set();
    assignedLoanTapeCsvConfig.fields.forEach((field) => {
        fieldSet.add(field);
    });
    switch (assignedLoanTapeCsvConfig.type) {
        // eagerly resolve include / exclude method
        // such that this does not need to be resolved for each receivable
        case 'exclude': {
            const useField = (field) => {
                return !fieldSet.has(field);
            };
            return useField;
        }
        case 'include': {
            const useField = (field) => {
                return fieldSet.has(field);
            };
            return useField;
        }
        default:
            return (0, common_1.finleyAssertUnreachable)(assignedLoanTapeCsvConfig.type);
    }
};
/**
 * Single place to create the extendedResponseTape data structure.
 * NOTE: The exact order of applying fields to the extendedResponseTape object is intentional, and any updates should be made with caution.
 * The output of this gets applied to our Servicer Report XLSX, so we have kept this less DRY on purpose, to ensure predictability and consistency
 * with what to expect in both the CSV and XLSX outputs.
 */
const createExtendedResponseTape = async ({ facilityType, multiLoanProductConcentrationLimits, loanProductMechanics, borrowingBaseReceivables: allBorrowingBaseReceivables, progressCallback, }) => {
    const { loanProductId } = loanProductMechanics;
    const useField = createAssignedCsvFieldAdapter({
        assignedLoanTapeCsvConfig: loanProductMechanics._config_.assignedLoanTapeCsvConfigs
            ?.extendedResponseTape,
    });
    const borrowingBaseReceivables = allBorrowingBaseReceivables.filter((receivable) => receivable.loanProductId === loanProductMechanics.loanProductId);
    const singleLPConcentrationLimitKeys = loanProductMechanics.concentrationLimitConfigs.map(({ propertyKey }) => propertyKey);
    const multiLPConcentrationLimitKeys = multiLoanProductConcentrationLimits.map(({ propertyKey }) => propertyKey);
    const concentrationLimitKeys = [
        ...singleLPConcentrationLimitKeys,
        ...multiLPConcentrationLimitKeys,
    ];
    const { updateProgress } = (0, progressHelper_1.createProgressHelper)({
        progressCallback,
        operation: 'Create extended response tape',
        numRows: borrowingBaseReceivables.length,
    });
    // HACK ALERT - to support valley bank and not disrupt other customers
    const vbLoanProductIds = getValleyBankLoanProductIds();
    // assigned csv config may allow hiding finleyReceivableId, but finleyReceivableId
    // is required for filter extended rrt to assigned extended rrt. we can map row index
    // for finleyReceivableId to preserve this information for assigned extended rrt consumption
    const rowIndexToFinleyReceivableIdMap = new Array(borrowingBaseReceivables.length);
    const mappedLoanTapeWithCalcs = borrowingBaseReceivables.map(async (receivable, index) => {
        await updateProgress(index);
        rowIndexToFinleyReceivableIdMap[index] = receivable.finleyReceivableId;
        // Type cast to BorrowingBaseReceivable to catch any issues when the
        // underlying type changes.
        // There is still a risk that the BorrowingBaseReceivable type changes and
        // we are reading older JSON. Perhaps the JSON need to have version field?
        // const receivableJson = calcRow.receivableJson as ReceivableTypes.CreditFacility.BorrowingBaseReceivable;
        const extendedResponseTape = {};
        if (useField(externalReceivableIdFieldName)) {
            extendedResponseTape[externalReceivableIdFieldName] =
                receivable.externalReceivableId;
        }
        if (useField(finleyReceivableIdFieldName)) {
            extendedResponseTape[finleyReceivableIdFieldName] =
                receivable.finleyReceivableId;
        }
        // output specific order based on the config, now that we allow reordering of fields
        loanProductMechanics._config_.baseFieldConfigs.forEach((baseFieldConfig) => {
            if (baseFieldConfig.field === 'finley_sanitized') {
                return; // Skip this field
            }
            const baseFieldName = generateFieldName({
                fieldPrefix: 'baseFields',
                fieldSuffix: baseFieldConfig.field,
            });
            if (useField(baseFieldName)) {
                extendedResponseTape[baseFieldName] =
                    receivable.baseFields[baseFieldConfig.field];
            }
        });
        // intermediate fields
        loanProductMechanics._config_.intermediateCalcConfigs.forEach((intermediateCalcConfig) => {
            const fieldName = intermediateCalcConfig.field;
            if ((0, RequiredIntermediateFields_1.isRequiredIntermediateFieldName)(fieldName)) {
                // @required-fields ignore excluded required intermediate fields
                if (!EXCLUDED_REQUIRED_INTERMEDIATE_FIELD_NAMES.includes(fieldName)) {
                    const reqFieldName = generateFieldName({
                        fieldPrefix: 'requiredIntermediateFields',
                        fieldSuffix: fieldName,
                    });
                    if (useField(reqFieldName)) {
                        extendedResponseTape[reqFieldName] =
                            receivable.requiredIntermediateFields[fieldName];
                    }
                }
            }
            else {
                const dynFieldName = generateFieldName({
                    fieldPrefix: 'dynamicIntermediateFields',
                    fieldSuffix: fieldName,
                });
                if (useField(dynFieldName)) {
                    extendedResponseTape[dynFieldName] =
                        receivable.dynamicIntermediateFields[fieldName];
                }
            }
        });
        // post processed fields
        Object.entries(receivable.postProcessedFields).forEach(([key, value]) => {
            const fieldName = generateFieldName({
                fieldPrefix: 'postProcessedFields',
                fieldSuffix: key,
            });
            if (useField(fieldName)) {
                // currently only postProcessedFields.normalizedExposure is allowed
                if (key === 'normalizedExposure') {
                    extendedResponseTape[fieldName] = value;
                }
            }
        });
        // valuation
        Object.entries(receivable.valuationFields).forEach(([key, value]) => {
            const fieldName = generateFieldName({
                fieldPrefix: 'valuationFields',
                fieldSuffix: key,
            });
            if (useField(fieldName)) {
                extendedResponseTape[fieldName] = value;
            }
        });
        // one column per eligibility criteria
        receivable.eligibilityStatuses.forEach(({ eligibilityStatus, eligibilityCriteriaKey }) => {
            const fieldName = generateFieldName({
                fieldPrefix: 'eligibilityStatus',
                fieldSuffix: eligibilityCriteriaKey,
            });
            if (useField(fieldName)) {
                extendedResponseTape[fieldName] = eligibilityStatus;
            }
        });
        Object.entries(receivable.eligibilityFields).forEach(([key, value]) => {
            const fieldName = generateFieldName({
                fieldPrefix: 'eligibilityFields',
                fieldSuffix: key,
            });
            if (useField(fieldName)) {
                extendedResponseTape[fieldName] = value;
            }
        });
        // concentration test
        // one column per concentration limit
        // looks for multiLP concentrations as well.
        let totalConcentrationReduction = 0;
        concentrationLimitKeys.forEach((propertyKey) => {
            const breachedConcentration = receivable.concentrationLimitReasons.find((concentrationLimitReason) => concentrationLimitReason.propertyKey === propertyKey);
            const concentrationKickout = breachedConcentration
                ? breachedConcentration.concentrationLimitKickout
                : 0;
            totalConcentrationReduction += concentrationKickout;
            // this only appears similar to the entry below for concentrationLimitFields, see comment below.
            // this is a bit dangerous if we ever (unlikely) have a concentration limit named "eligibleExposureLessConcentrationLimitKickouts"
            const fieldName = generateFieldName({
                fieldPrefix: 'concentrationLimitFields',
                fieldSuffix: propertyKey,
            });
            if (useField(fieldName)) {
                extendedResponseTape[fieldName] = concentrationKickout;
            }
        });
        // total conc reduction
        if (useField(concentrationLimitFieldsTotalConcentrationReductionField)) {
            extendedResponseTape[concentrationLimitFieldsTotalConcentrationReductionField] = totalConcentrationReduction;
        }
        // credit facility receivable concentration fields
        Object.entries(receivable.concentrationLimitFields).forEach(([key, value]) => {
            // while this looks similar to the above concentration limit field entry, it is different.
            // receivable.concentrationLimitFields only has one property currently that resolves to `concentrationLimitFields_eligibleExposureLessConcentrationLimitKickouts`
            // the early entry creates concentrationLimitFields_<concentrationLimitName> with the kickout for each concentration limit
            const fieldName = generateFieldName({
                fieldPrefix: 'concentrationLimitFields',
                fieldSuffix: key,
            });
            if (useField(fieldName)) {
                extendedResponseTape[fieldName] = value;
            }
        });
        Object.entries(receivable.borrowingBaseFields).forEach(([key, value]) => {
            const typedKey = key;
            // IF NOT(EXCLUDED FIELD) OR VALLEY BANK
            if (!EXCLUDED_BORROWING_BASE_FIELD_NAMES.includes(typedKey) ||
                vbLoanProductIds.includes(loanProductId)) {
                const fieldName = generateFieldName({
                    fieldPrefix: 'borrowingBaseFields',
                    fieldSuffix: key,
                });
                if (useField(fieldName)) {
                    extendedResponseTape[fieldName] = value;
                }
            }
        });
        generallySupportedAssignmentFields.forEach((field) => {
            const fieldName = generateFieldName({
                fieldPrefix: 'assignmentFields',
                fieldSuffix: field,
            });
            if (useField(fieldName)) {
                extendedResponseTape[fieldName] =
                    receivable.assignmentFields?.[field] ?? null;
            }
        });
        if (useField(assignmentFieldsReceivableStatusFieldName))
            extendedResponseTape[assignmentFieldsReceivableStatusFieldName] = receivable.assignmentFields
                ? finley_types_1.Receivable.ReceivableStatus.Assigned
                : null;
        if (facilityType === 'forwardFlow') {
            forwardFlowSupportedAssignmentFields.forEach((field) => {
                const fieldName = generateFieldName({
                    fieldPrefix: 'assignmentFields',
                    fieldSuffix: field,
                });
                if (useField(fieldName)) {
                    extendedResponseTape[fieldName] =
                        receivable.assignmentFields?.[field] ?? null;
                }
            });
        }
        return extendedResponseTape;
    });
    const res = await Promise.all(mappedLoanTapeWithCalcs);
    return { extendedResponseTape: res, rowIndexToFinleyReceivableIdMap };
};
exports.createExtendedResponseTape = createExtendedResponseTape;
/**
 * Filters an existing extended response tape
 * @returns an extended response tape with only the receivables
 * that were assigned with the specified reportingDate and fileVersion
 */
const createNewlyAssignedExtendedResponseTape = ({ extendedResponseTape, creditFacilityId, reportingDate, fileVersion, receivableResponseTape, creditFacilitySlug, rowIndexToFinleyReceivableIdMap, }) => {
    const receivableMap = new Map(receivableResponseTape.receivables.map((receivable) => [
        receivable.finleyReceivableId,
        receivable,
    ]));
    return extendedResponseTape.filter((_unused, idx) => {
        const finleyReceivableId = rowIndexToFinleyReceivableIdMap[idx];
        (0, common_1.finleyAssert)(typeof finleyReceivableId === 'string', `[finleyReceivableId:${finleyReceivableId}], [creditFacilitySlug:${creditFacilitySlug}] from extended response tape must be a string, [found:${typeof finleyReceivableId}]`);
        const receivable = receivableMap.get(finleyReceivableId);
        (0, common_1.finleyAssert)(receivable && receivable.assignmentFields, `assignmentFields must exist for [finleyReceivableId:${finleyReceivableId}] [creditFacilitySlug:${creditFacilitySlug}]`);
        return (receivable.assignmentFields.creditFacilityId === creditFacilityId &&
            receivable.assignmentFields.fileVersion === fileVersion &&
            receivable.assignmentFields.reportingDate === reportingDate);
    });
};
exports.createNewlyAssignedExtendedResponseTape = createNewlyAssignedExtendedResponseTape;
//# sourceMappingURL=extendedResponseTape.js.map